/**
 * Google Ads conversion configuration — SINGLE SOURCE OF TRUTH.
 *
 * Live values come from the Google Ads UI snippet:
 *   gtag('event','conversion',{'send_to':'AW-18431332404/jwymCOyEq-8cELSg39RE'})
 *
 * One account + one conversion action serves all four campaigns (Solar,
 * Roofing, General Contracting, EV); the campaign a click came from is
 * resolved by Google, not by us, so no campaign-specific labels are needed.
 *
 * Nothing is sent while the ID or the label is `null`, and nothing is sent
 * without advertising consent. Never invent placeholder values.
 *
 * Privacy contract (enforced in `ads-tracking.ts`) — scoped to OUR payload:
 *   - enhanced conversions are off (in the Ads UI and in our tag config)
 *   - our custom payload carries no names, emails, phones, addresses,
 *     CRM/HubSpot IDs, raw URLs or query strings; the only per-conversion
 *     identifier is a random, non-PII `transaction_id` for de-duplication
 *   - the Google tag itself still performs its own standard collection
 *     (cookies/first-party identifiers, page URL, referrer, user agent,
 *     coarse IP-derived location). We limit what WE add; we do not and cannot
 *     claim the tag collects nothing.
 */

/** Google Ads conversion ID. */
export const GOOGLE_ADS_CONVERSION_ID: string | null = "AW-18431332404";

/** Conversion actions we report. Keys are internal, stable names. */
export type AdsConversionKey = "website_lead_successful_submission";

export interface AdsConversionAction {
  /** Human name of the action as configured in Google Ads. */
  readonly googleAdsName: string;
  /** Conversion label from Google Ads. `null` until provided. */
  readonly label: string | null;
  /** Google Ads "Count" setting — informational, mirrors the Ads config. */
  readonly count: "one" | "every";
  /** Whether this is a primary conversion action in Google Ads. */
  readonly primary: boolean;
}

export const ADS_CONVERSIONS: Record<AdsConversionKey, AdsConversionAction> = {
  website_lead_successful_submission: {
    googleAdsName: "Website Lead - Successful Submission",
    label: "jwymCOyEq-8cELSg39RE",
    count: "one",
    primary: true,
  },
};

const AW_ID_RE = /^AW-\d{6,}$/;

/** True only when a syntactically valid Ads conversion ID is configured. */
export function isAdsAccountConfigured(): boolean {
  return typeof GOOGLE_ADS_CONVERSION_ID === "string" && AW_ID_RE.test(GOOGLE_ADS_CONVERSION_ID);
}

/**
 * Build the `send_to` value for a conversion, or `null` when the account ID or
 * the action label is still unknown. `null` means: send nothing.
 */
export function getConversionSendTo(key: AdsConversionKey): string | null {
  if (!isAdsAccountConfigured()) return null;
  const action = ADS_CONVERSIONS[key];
  if (!action || !action.label) return null;
  return `${GOOGLE_ADS_CONVERSION_ID}/${action.label}`;
}

/**
 * CALL conversion — "Website Calls - 60 Seconds" (primary, phone call lead,
 * count One, 60-second minimum, 30-day click window). Google's own snippet:
 *
 *   gtag('config','AW-18431332404/FUQbCOD_6PIcELSg39RE',
 *        {'phone_conversion_number':'(310) 850-7223'})
 *
 * The number below is the DISPLAYED business number Google matches on; the
 * forwarding number arrives via `phone_conversion_callback`. No conversion
 * event is ever sent from a click — Google measures the forwarded call.
 */
export const CALL_CONVERSION_LABEL: string | null = "FUQbCOD_6PIcELSg39RE";

/** `send_to`/config destination for the call conversion, or null. */
export function getCallConversionSendTo(): string | null {
  if (!isAdsAccountConfigured()) return null;
  if (!CALL_CONVERSION_LABEL) return null;
  return `${GOOGLE_ADS_CONVERSION_ID}/${CALL_CONVERSION_LABEL}`;
}

